<template>
  <div id="item-history">

    <transition name="slide-left">
      <div class="credit-note-slide-panel bg-theme-600 p-3" v-if="creditNoteEditID">

        <button class="text-link" @click="creditNoteEditID = null">Close</button>

        <div class="contents">
          <div v-if="hasPermission('credit-note.edit')" class="text-center leading-none">
            <p class="text-lg">Credit note preview</p>
            <a :href="'/credit-note/edit/' + creditNoteEditID" target="_blank" class="text-orange text-sm">
              Edit #{{ creditNoteEditID }}
              <i class="fas fa-external-link-alt"></i>
            </a>
          </div>
          <credit-note-edit
            :creditNoteID="creditNoteEditID"
            :in-modal="true"
            :preSelectedProductID="product.id"
          ></credit-note-edit>
        </div>

      </div>
    </transition>

    <transition name="fade">
      <div class="slide-bg" v-if="active" @click="close"></div>
    </transition>

    <transition name="slide-right">
      <div class="slide-panel bg-theme-600 border-l border-b border-theme-200 light:border-theme-600 p-3" v-if="active">

        <div class="flex justify-end">
          <button class="text-link" @click="close">Close</button>
        </div>

        <p class="text-2xl font-bold mt-4 border-b border-theme-200 light:border-theme-600">Product</p>

        <div class="relative border-b border-theme-200 light:border-theme-600">
          <product-information
            :product="product"
            :edit="true"
            @updated="$emit('updated-product', $event)"
          ></product-information>
        </div>

        <div class="mt-4">
          <p class="text-2xl font-bold">Credit note History</p>
          <div class="flex flex-wrap -mx-2 mt-2">
            <div class="flex-1 px-2">
              <button
                class="btn btn-sm w-full focus:outline-none"
                :class="type === 'purchases' ? 'btn-red' : 'border border-gray-700 light:border-gray-300'"
                @click="$emit('update:type', 'purchases')"
              >Purchases</button>
            </div>
            <div class="flex-1 px-2">
              <button
                class="btn btn-sm w-full focus:outline-none"
                :class="type === 'sales' ? 'btn-green' : 'border border-gray-700 light:border-gray-300'"
                @click="$emit('update:type', 'sales')"
              >Sales</button>
            </div>
          </div>

          <div class="box">
            <p class="text-lg text-center my-12" v-if="!Object.entries(creditNoteItems).length">No data to display</p>
            <div class="w-full overflow-x-auto" v-else>
              <table class="table table-striped">
                <thead>
                  <tr>
                    <th v-for="(head, i) in headers" :key="'table-headings-' + i">
                      <div class="flex items-center justify-between">
                        <p>{{ head.display }}</p>
                        <a class="text-link " @click="setSortBy(head)" v-if="head.sortable">
                          <i class="fas" v-if="head.identifier === sortBy" :class="sortOrder === 'desc' ? 'fa-sort-down' : 'fa-sort-up'"></i>
                          <i class="fas fa-sort" v-else></i>
                        </a>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, i) in creditNoteItems" :key="i" :class="{ 'selected': creditNoteEditID === item.credit_note_id }">
                    <td>{{ $moment(item.created_at).format(config.dateTimeFormats.display) }}</td>
                    <td>{{ $formatMoney(item.value) }}</td>
                    <td>
                      <span class="font-bold">{{ $formatMoney(item.total.all) }}</span>
                    </td>
                    <td>{{ type }}</td>
                    <td>
                      <a class="text-link bg-theme-600 hover:bg-theme-700 rounded px-2 py-1" @click="opencreditNote(item.credit_note_id)">
                        preview
                      </a>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="w-full flex justify-center">
              <pagination-component
                class="mt-8"
                v-if="Object.entries(creditNoteItems).length && pagination.totalRows / pagination.perPage > 1"
                v-model="pagination.currentPage"
                :total-rows="pagination.totalRows"
                :rows-per-page="pagination.perPage"
              />
            </div>

          </div>
        </div>

      </div>
    </transition>

  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
const ProductInformation = resolve => require(['@/components/pages/ProductInformation'], resolve)
const PaginationComponent = resolve => require(['@/components/Pagination'], resolve)

export default {
  name: 'credit-note-item-history',
  props: {
    active: Boolean,
    product: {},
    type: {
      type: String,
      default: 'purchases',
    },
  },
  components: {
    ProductInformation,
    PaginationComponent,
  },
  data() {
    return {
      creditNoteItems: [],
      searchInput: '',
      creditNoteEditID: null,
      headers: [
        {identifier: 'created_at', display: 'Date', sortable: true},
        {identifier: 'value', display: 'Item price', sortable: true},
        {identifier: 'total', display: 'Total', sortable: false},
        {identifier: 'type', display: 'Type', sortable: false},
        {identifier: 'creditNote', display: 'Credit note', sortable: false},
      ],
      sortBy: 'created_at',
      sortOrder: 'desc',
      query: {
        search: '',
      },
      pagination: {
        currentPage: 1,
        totalRows: 0,
        perPage: 0,
      },
      modal: {
        creditNote: {
          active: false,
          title: '',
        },
      },
    }
  },
  watch: {
    active() {
      if (this.active) {
        document.body.classList.add('no-scroll')
      } else {
        document.body.classList.remove('no-scroll')
      }
    },
    'pagination.currentPage'() {
      this.get()
    },
    product() {
      if (!this.product.id) {
        // show error alert
        return
      }
      this.get()
    },
    type() {
      this.get()
    },
    creditNoteItems() {
      this.creditNoteItems.map(v => {
        v.total = {
          raw: v.value * v.quantity,
        }
        v.total.discount = v.total.raw * v.discount / 100
        v.total.tax = (v.total.raw - v.total.discount) * v.tax / 100
        v.total.all = v.total.raw - v.total.discount + v.total.tax
      })
    },
  },
  computed: {
    ...mapGetters({
      config: 'config/config',
      hasPermission: 'user/hasPermission',
    })
  },
  beforeCreate() {
    this.$options.components.creditNoteEdit = require('@/components/pages/credit-notes/Edit').default
  },
  methods: {
    close() {
      this.closecreditNote()
      this.$emit('update:active', false)
    },
    closecreditNote() {
      this.creditNoteEditID = null
      this.modal.creditNote.active = false
    },
    opencreditNote(creditNoteID) {
      this.closecreditNote()
      if (this.creditNoteEditID === creditNoteID) {
        return
      }
      this.creditNoteEditID = creditNoteID
      this.modal.creditNote.active = true
    },
    setSortBy(heading) {
      if (!heading.sortable) {
        return
      }

      if (this.sortBy === heading.identifier) {
        this.sortOrder = this.sortOrder === 'desc' ? 'asc' : 'desc'
      } else {
        this.sortBy = heading.identifier
        this.sortOrder = 'desc'
      }
      this.get()
    },
    get() {
      axios.get('credit-note-items', {params:{
        productID: this.product.id,
        type: this.type,
        search: this.searchInput,
        page: this.pagination.currentPage,
        sortBy: this.sortBy,
        sortOrder: this.sortOrder,
      }})
        .then(response => {
          if (response.data) {
            const res = response.data
            this.pagination.perPage = res.per_page
            this.pagination.totalRows = res.total
            this.creditNoteItems = Object.assign([], res.data)

            this.query.search = this.searchInput
          }
        })
    }
  }
}
</script>

